import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ModulesContext, {
  MODULE_PERMISSION_CACHE_KEY,
} from "./context/ModulesContext";
import { resolveDefaultModulePath } from "./util/defaultModule";
import {
  getSupportedLocale,
  loadLocalTranslations,
} from "./services/localeLoader";
import SupportBanner from "./component/SupportBanner";
import ToastProvider from "./component/ToastProvider";
import { supportBannerFor } from "./services/accessContext";
import { switchCompany } from "./services/companySwitch";
import { showApiError } from "./services/apiError";

/**
 * 403 — where ProtectedRoute sends an account that is missing the permission a
 * page requires.
 *
 * The route sits OUTSIDE DashboardLayout, so neither the Header (which fills
 * window.languageContext) nor the sidebar is on screen: the page carries its
 * own branding and loads its own translations when it is opened cold.
 */

/* Card entrance and the pulse on the locked row of the illustration. Both are
   decoration, so both are dropped under prefers-reduced-motion.

   The rest is the short-phone budget. This route renders outside
   DashboardLayout, so there is no header and no BottomTabBar: if the actions
   are off screen the page has no visible way out at all. Below `lg` they live
   in a bar pinned to the bottom of the viewport (see fx403-actionbar), and on
   a short viewport the decorative strip gives up most of its height so the
   copy above the bar still reads without scrolling. */
const decorCss = `
@keyframes fx403-rise {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
}
@keyframes fx403-pulse {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.45; }
}
.fx403-card { animation: fx403-rise 540ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.fx403-pulse { animation: fx403-pulse 4.5s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .fx403-card, .fx403-pulse { animation: none; }
}
/* The pinned bar, and the room the page leaves for it. iOS home-indicator
   safe area is added on top of the padding, never instead of it. */
@media (max-width: 1023px) {
  .fx403-page { padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
  .fx403-actionbar { padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }
}
/* iPhone SE / 8 and any landscape phone: 667px tall or less. */
@media (max-width: 1023px) and (max-height: 700px) {
  .fx403-panel { padding-top: 10px; padding-bottom: 10px; }
  .fx403-art { height: 58px; }
  .fx403-title { margin-top: 14px; font-size: 22px; }
  .fx403-body { margin-top: 8px; font-size: 14px; }
  .fx403-steps { margin-top: 14px; padding: 12px; }
  .fx403-copy { padding: 18px; }
}
`;

/**
 * The permission list the account cannot get past: two rows it holds, one
 * locked and lit up. Decorative — no copy inside, so nothing to translate.
 */
const PermissionCardArt = () => (
  <svg
    viewBox="0 0 260 190"
    className="fx403-art h-[104px] w-auto sm:h-[124px] lg:h-[190px]"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      x="26"
      y="18"
      width="208"
      height="154"
      rx="18"
      fill="#ffffff"
      fillOpacity="0.10"
      stroke="#ffffff"
      strokeOpacity="0.30"
    />
    {/* header row */}
    <circle cx="52" cy="46" r="10" fill="#ffffff" fillOpacity="0.38" />
    <rect
      x="70"
      y="41"
      width="66"
      height="6"
      rx="3"
      fill="#ffffff"
      fillOpacity="0.42"
    />
    <rect
      x="70"
      y="52"
      width="40"
      height="5"
      rx="2.5"
      fill="#ffffff"
      fillOpacity="0.26"
    />
    <path
      d="M26 72h208"
      stroke="#ffffff"
      strokeOpacity="0.22"
      strokeWidth="1"
    />
    {/* two granted rows */}
    {[90, 118].map((y) => (
      <g key={y}>
        <rect
          x="44"
          y={y}
          width="108"
          height="7"
          rx="3.5"
          fill="#ffffff"
          fillOpacity="0.3"
        />
        <rect
          x="196"
          y={y - 5}
          width="18"
          height="18"
          rx="6"
          fill="#ffffff"
          fillOpacity="0.2"
        />
        <path
          d="M200.5 3.5l3 3 6-6"
          transform={`translate(0 ${y})`}
          stroke="#ffffff"
          strokeOpacity="0.8"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ))}
    {/* the locked row */}
    <g className="fx403-pulse">
      <rect
        x="30"
        y="135"
        width="200"
        height="34"
        rx="13"
        fill="#3AC0C4"
        fillOpacity="0.16"
      />
      <rect
        x="34"
        y="139"
        width="192"
        height="26"
        rx="9"
        fill="#3AC0C4"
        fillOpacity="0.38"
      />
      <rect
        x="44"
        y="148.5"
        width="128"
        height="7"
        rx="3.5"
        fill="#EAFBFB"
        fillOpacity="0.95"
      />
      <rect x="196" y="143" width="18" height="18" rx="6" fill="#EAFBFB" />
      <rect
        x="201"
        y="151"
        width="8"
        height="6"
        rx="1.6"
        fill="#0D5759"
      />
      <path
        d="M202.6 151v-1.6a2.4 2.4 0 0 1 4.8 0V151"
        stroke="#0D5759"
        strokeWidth="1.2"
      />
    </g>
  </svg>
);

const PersonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.8 19.5a7.2 7.2 0 0 1 14.4 0" />
  </svg>
);

const CompassIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="8.5" />
    <path d="M15 9l-1.7 4.3L9 15l1.7-4.3L15 9z" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M5 12h13" />
    <path d="M12.5 6.5 18 12l-5.5 5.5" />
  </svg>
);

const LockGlyph = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.8" />
    <path d="M8 10.5V8a4 4 0 1 1 8 0v2.5" />
  </svg>
);

/**
 * window.languageContext holds a dictionary but not the locale it belongs to.
 * Header writes localStorage "language" in the same breath, so that is the
 * label for whatever is currently in the global — and it is what tells us
 * whether the global matches the locale in the URL or is left over from a
 * previous one.
 */
const contextTranslationsFor = (
  locale: string
): Record<string, string> | null => {
  const loaded = window.languageContext?.language;
  if (!loaded) return null;
  return getSupportedLocale(localStorage.getItem("language") || "en") === locale
    ? loaded
    : null;
};

/**
 * The permission payload DashboardLayout cached on its last successful load.
 * This route lives outside DashboardLayout, so on a cold open (a reload, a
 * pasted /{lang}/403 link) ModulesContext is still empty and the un-cached
 * resolver has nothing to go on but /dashboard — the one destination a role
 * that just hit 403 may well not be allowed to open either.
 */
const cachedModules = (): any[] => {
  try {
    const raw = localStorage.getItem(MODULE_PERMISSION_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function ForbiddenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { modules } = useContext(ModulesContext);

  const lang = getSupportedLocale(
    location.pathname.split("/")[1] || localStorage.getItem("language") || "en"
  );

  // LanguageContext has no provider in this app; the shell's working source of
  // translations is window.languageContext, which Header fills in and announces
  // with a "languageLoaded" DOM event.
  const [language, setLanguage] = useState<Record<string, string>>(
    () => contextTranslationsFor(lang) ?? {}
  );

  useEffect(() => {
    let cancelled = false;
    // Adopt the global only when it holds THIS locale; a session running in
    // English that follows a pasted /al/403 link must not render English copy
    // under an /al/ URL.
    const adopt = () => {
      const loaded = contextTranslationsFor(lang);
      if (loaded && !cancelled) setLanguage(loaded);
      return Boolean(loaded);
    };
    // Opened cold there is no Header to have filled the global at all — fetch
    // the locale file directly.
    if (!adopt()) {
      loadLocalTranslations(lang).then((translations) => {
        if (!cancelled) setLanguage(translations);
      });
    }
    document.addEventListener("languageLoaded", adopt);
    return () => {
      cancelled = true;
      document.removeEventListener("languageLoaded", adopt);
    };
  }, [lang]);

  const t = (key: string): string => language?.[key] ?? key;

  // A history entry for a permission error should not be indistinguishable
  // from every other page in the tab strip and the back-button menu.
  useEffect(() => {
    const previous = document.title;
    document.title = t("Permission required");
    return () => {
      document.title = previous;
    };
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Somewhere the user can actually go: /dashboard when they are permitted to
  // open it, otherwise the first module their role does include.
  const goHome = () => {
    const known = modules?.length ? modules : cachedModules();
    navigate(`/${lang}${resolveDefaultModulePath(known)}`);
  };

  // ProtectedRoute redirects with `replace`, so the entry behind this one is
  // the last page the user was allowed to open — but only if there IS one.
  // react-router stamps every history entry it owns with an index, and the
  // first entry of the session is index 0; anything above that has an in-app
  // entry behind it. window.history.length cannot tell us that: it counts
  // entries from other origins too (and never goes down), so it would send a
  // user who followed a deep link from their mail client back out of the
  // product entirely.
  const goBack = () => {
    const entryIndex = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof entryIndex === "number" && entryIndex > 0) {
      navigate(-1);
    } else {
      goHome();
    }
  };

  // The layout's context survives the redirect here; a cold open falls back to the token's claim.
  const supportBanner = supportBannerFor(window.accessContext);

  const backToInnovX = async () => {
    if (!supportBanner?.homeCompanyId) return;
    try {
      await switchCompany(supportBanner.homeCompanyId, { resume: () => backToInnovX() });
    } catch (error) {
      showApiError(error, { language });
    }
  };

  const focusRing =
    "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3AC0C4] focus-visible:ring-offset-2 focus-visible:ring-offset-white";

  // px-4 below `lg`: the two buttons share one 375px-wide row, and Albanian
  // ("Shko te Ballina") needs the width back from the padding to stay on one
  // line. The primary gets the larger share of the row for the same reason.
  const actionBase = `inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-[30px] px-4 text-sm font-medium transition-colors lg:px-6 ${focusRing}`;

  /**
   * One set of actions, rendered in the pinned bar below `lg` and inside the
   * card from `lg` up — never both at once (each wrapper hides at the other's
   * breakpoint, so only one pair is in the DOM's a11y tree at a time).
   */
  const actions = (
    <>
      <button
        type="button"
        onClick={goHome}
        className={`${actionBase} flex-[1.4] bg-[#0F7679] text-white hover:bg-[#0D5759] lg:flex-none`}
      >
        {t("Go to Home")}
        <ArrowIcon />
      </button>
      <button
        type="button"
        onClick={goBack}
        className={`${actionBase} flex-1 border border-[#DBE4E4] bg-white text-[#0D5759] hover:bg-[#EBF0F0] lg:flex-none`}
      >
        {t("Go back")}
      </button>
    </>
  );

  return (
    <>
    {supportBanner && (
      <>
        <SupportBanner
          companyName={supportBanner.companyName}
          language={language}
          canGoBack={!!supportBanner.homeCompanyId}
          onBack={backToInnovX}
        />
        <ToastProvider />
      </>
    )}
    <div
      className="fx403-page relative flex min-h-screen w-full items-center justify-center bg-[#F5F9F9] px-4 py-8 font-inter sm:px-6 sm:py-12"
      style={{ minHeight: "calc(100vh - var(--finex-banner-h))" }}
    >
      <style>{decorCss}</style>

      {/* Decoration: two teal glows over a blueprint grid that fades out. */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute left-1/2 top-[-260px] h-[560px] w-[900px] max-w-none -translate-x-1/2 rounded-full blur-[130px]"
          style={{ backgroundColor: "rgba(58, 192, 196, 0.20)" }}
        />
        <div
          className="absolute bottom-[-300px] right-[-200px] h-[560px] w-[560px] rounded-full blur-[140px]"
          style={{ backgroundColor: "rgba(15, 118, 121, 0.16)" }}
        />
        <svg className="absolute inset-0 h-full w-full">
          <defs>
            <pattern
              id="fx403-grid"
              width="34"
              height="34"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M34 0H0V34"
                fill="none"
                stroke="#0D5759"
                strokeOpacity="0.08"
                strokeWidth="1"
              />
            </pattern>
            <radialGradient id="fx403-fade" cx="50%" cy="45%" r="60%">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </radialGradient>
            <mask id="fx403-mask">
              <rect width="100%" height="100%" fill="url(#fx403-fade)" />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#fx403-grid)"
            mask="url(#fx403-mask)"
          />
        </svg>
      </div>

      <main className="relative z-10 w-full max-w-[980px]">
        <section
          className="fx403-card overflow-hidden rounded-[24px] border border-[#DBE4E4] bg-white shadow-[0_34px_70px_-46px_rgba(13,87,89,0.6)] lg:grid lg:grid-cols-[1fr_340px]"
          aria-labelledby="fx403-title"
        >
          {/* Branded panel: a strip above the copy on phones, the right-hand
              column on desktop. Purely decorative. */}
          <div
            className="fx403-panel relative flex items-center justify-center overflow-hidden px-6 py-5 lg:order-2 lg:py-10"
            style={{
              backgroundImage:
                "linear-gradient(152deg, #0F7679 0%, #0D5759 58%, #0A4446 100%)",
            }}
            aria-hidden="true"
          >
            <div
              className="absolute left-[-70px] top-[-120px] h-[300px] w-[300px] rounded-full blur-[80px]"
              style={{ backgroundColor: "rgba(58, 192, 196, 0.55)" }}
            />
            <svg className="absolute inset-0 h-full w-full opacity-[0.35]">
              <defs>
                <pattern
                  id="fx403-panel-grid"
                  width="26"
                  height="26"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M26 0H0V26"
                    fill="none"
                    stroke="#EAFBFB"
                    strokeOpacity="0.14"
                    strokeWidth="1"
                  />
                </pattern>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="url(#fx403-panel-grid)"
              />
            </svg>
            <div className="relative">
              <PermissionCardArt />
            </div>
          </div>

          <div className="fx403-copy order-1 p-6 sm:p-9 lg:p-10">
            <span className="inline-flex items-center gap-[6px] rounded-full bg-[#EBF0F0] px-3 py-[6px] text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0D5759]">
              <LockGlyph />
              {t("Error 403")}
            </span>

            <h1
              id="fx403-title"
              className="fx403-title mt-5 text-[25px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#222525] sm:text-[32px]"
            >
              {t("You need permission to open this page")}
            </h1>

            <p className="fx403-body mt-3 max-w-[52ch] text-[15px] leading-[1.6] text-[#4A5353]">
              {t(
                "Your account doesn't include the permission this page requires. Nothing is broken — this page simply isn't part of your role."
              )}
            </p>

            <div className="fx403-steps mt-6 rounded-[16px] border border-[#DBE4E4] bg-[#F7FBFB] p-4 sm:p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0D5759]">
                {t("What you can do")}
              </p>
              <ul className="mt-3 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="mt-[1px] flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] border border-[#DBE4E4] bg-white text-[#0F7679]">
                    <PersonIcon />
                  </span>
                  <span className="text-[14px] leading-[1.55] text-[#4A5353]">
                    {t(
                      "Ask an administrator on your team to add this permission to your role."
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-[1px] flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] border border-[#DBE4E4] bg-white text-[#0F7679]">
                    <CompassIcon />
                  </span>
                  <span className="text-[14px] leading-[1.55] text-[#4A5353]">
                    {t(
                      "Until then, you can keep working from the pages your role already includes."
                    )}
                  </span>
                </li>
              </ul>
            </div>

            <div className="mt-6 hidden gap-3 lg:flex lg:items-center">
              {actions}
            </div>
          </div>
        </section>
      </main>

      {/* Below `lg` the way out is pinned to the bottom of the viewport. There
          is no header and no bottom tab bar on this route, so an action that
          needs a scroll to reach is a screen with no way off it — measured at
          375x667 the in-flow pair started at y=668, one pixel past the fold. */}
      <div className="fx403-actionbar fixed inset-x-0 bottom-0 z-20 border-t border-[#DBE4E4] bg-white/95 px-4 pt-3 backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-[520px] items-center gap-3">
          {actions}
        </div>
      </div>
    </div>
    </>
  );
}
